/* ============================================================================
   Rotas do MoviZap.
   ----------------------------------------------------------------------------
   Toda rota de tela carrega `meta.codigo` — o mesmo código imutável do
   `movizap/telas.py`. É ele que a barra de status exibe e que a guarda usa.

   ⚠️ Rota sem `meta.codigo` não é tela: ou é pública (login), ou é auxiliar
   (sem permissão, não encontrada). Uma tela nova só entra aqui DEPOIS de
   existir no registro do backend — senão a guarda a barra na hora.

   As rotas listadas são as telas de FASE 1, e são exatamente as mesmas do
   `movizap/telas.py`. As reservadas (ATD_3.1, ATD_4.1, CFG_2.2, REL_1.1) não
   têm rota de propósito: o código está ocupado, a tela não existe.

   🚨 REGISTRO E ROTEADOR TÊM QUE ANDAR JUNTOS. O menu é gerado do registro do
   backend, então uma tela registrada lá e ausente daqui aparece no menu e
   leva para "não encontrada" -- e ninguém descobre até alguém clicar.
   Aconteceu em 06/08 com a ATD_5.1. O `teste_telas.py` agora compara os dois
   lados e reprova a divergência.
   ============================================================================ */
import { createRouter, createWebHistory } from 'vue-router'

import { sessao, autenticado, codigosPermitidos, restaurar } from '../estado/sessao.js'
import { temToken } from '../api/cliente.js'

import Login from '../telas/Login.vue'
import Inicio from '../telas/Inicio.vue'
import Email from '../telas/Email.vue'
import Canais from '../telas/Canais.vue'
import Clientes from '../telas/Clientes.vue'
import Contatos from '../telas/Contatos.vue'
import CaixaDeEntrada from '../telas/CaixaDeEntrada.vue'
import Fila from '../telas/Fila.vue'
import Informativos from '../telas/Informativos.vue'
import Historico from '../telas/Historico.vue'
import Ligacoes from '../telas/Ligacoes.vue'
import Agendamentos from '../telas/Agendamentos.vue'
import Orcamentos from '../telas/Orcamentos.vue'
import Tecnicos from '../telas/Tecnicos.vue'
import FichaDoTecnico from '../telas/FichaDoTecnico.vue'
import ChatInterno from '../telas/ChatInterno.vue'
import Solicitacoes from '../telas/Solicitacoes.vue'
import PainelOperacao from '../telas/PainelOperacao.vue'
import Atendentes from '../telas/Atendentes.vue'
import Times from '../telas/Times.vue'
import Automacao from '../telas/Automacao.vue'
import Classificacoes from '../telas/Classificacoes.vue'
import IaPrompt from '../telas/IaPrompt.vue'
import Sincronizacao from '../telas/Sincronizacao.vue'
import RegistroDeTelas from '../telas/RegistroDeTelas.vue'
// A casca das abas. As seis telas acima continuam existindo e são montadas
// POR ELA -- os imports ficam porque o `teste_router.py` lê este arquivo e
// porque tirá-los não ganharia nada.
import Configuracoes from '../telas/Configuracoes.vue'
import SemPermissao from '../telas/SemPermissao.vue'
import NaoEncontrada from '../telas/NaoEncontrada.vue'

const rotas = [
  {
    path: '/login',
    name: 'login',
    component: Login,
    meta: { publica: true, titulo: 'Entrar' },
  },

  {
    path: '/inicio',
    name: 'INI_1.1',
    component: Inicio,
    meta: { codigo: 'INI_1.1', titulo: 'Início' },
  },

  {
    path: '/email',
    name: 'EML_1.1',
    component: Email,
    meta: { codigo: 'EML_1.1', titulo: 'E-mail' },
  },

  // ---- ATD: atendimento ----
  {
    path: '/atendimento',
    name: 'ATD_1.1',
    component: CaixaDeEntrada,
    meta: { codigo: 'ATD_1.1', titulo: 'WhatsApp', cheio: true },
  },
  {
    // antes de /atendimento/:id — senão "fila" vira um id
    path: '/atendimento/fila',
    name: 'ATD_1.3',
    component: Fila,
    meta: { codigo: 'ATD_1.3', titulo: 'Fila' },
  },
  {
    // antes de /atendimento/:id, pela mesma razão que a fila
    path: '/atendimento/historico',
    name: 'ATD_5.1',
    component: Historico,
    meta: { codigo: 'ATD_5.1', titulo: 'Histórico' },
  },
  {
    // 🔵 Decisão 14 dele (19/10): Agendamentos é tela, em Atendimento, aberta
    // a todo atendente. Fora de /atendimento no caminho porque não é uma
    // conversa: é o calendário do que foi combinado nas conversas.
    path: '/agendamentos',
    name: 'ATD_7.1',
    component: Agendamentos,
    meta: { codigo: 'ATD_7.1', titulo: 'Agendamentos' },
  },
  {
    // 🔵 26/10: a aba de consulta dos orçamentos emitidos. Fora de
    // /atendimento no caminho pelo mesmo motivo do /agendamentos: não é uma
    // conversa, é o que saiu das conversas -- com número de documento.
    path: '/orcamentos',
    name: 'ORC_1.1',
    component: Orcamentos,
    meta: { codigo: 'ORC_1.1', titulo: 'Orçamentos' },
  },
  {
    // 🔵 04/11: a ficha do técnico, *"abaixo de 'orçamentos'"* -- e o caminho
    // segue a mesma régua da ORC_1.1 e do /agendamentos: não é conversa, é
    // cadastro de quem executa o serviço.
    //
    // ⚠️ NÃO EXISTE ROTA PARA A `TEC_1.2`, e isso é de propósito: ela não é
    // tela, é a chave que as rotas de escrita exigem. A guarda de rota usa
    // `sessao.telas`, que continua trazendo as telas-aba -- e é de lá que esta
    // tela lê se desenha os botões.
    path: '/tecnicos',
    name: 'TEC_1.1',
    component: Tecnicos,
    meta: { codigo: 'TEC_1.1', titulo: 'Técnicos' },
  },
  {
    // 🚨 A `TEC_1.2` NÃO DESENHA TELA: ela é a permissão de escrever (criar
    // técnico, mexer no valor, credenciar), e quem a lê é a própria TEC_1.1
    // para decidir se mostra os botões.
    //
    // ⚠️ MESMO ASSIM ELA PRECISA DE ROTA, e isto foi achado pela suíte
    // (`teste_router.py`): toda tela do registro tem de existir aqui, porque
    // `aba_de` tira do menu mas não impede alguém de chegar pelo endereço --
    // e aí a tela certa seria "não encontrada". Daí o redirecionamento: cai
    // em /tecnicos, que é onde se credencia de verdade.
    path: '/tecnicos/credenciar',
    name: 'TEC_1.2',
    redirect: { name: 'TEC_1.1' },
    meta: { codigo: 'TEC_1.2', titulo: 'Credenciamento de técnicos' },
  },
  {
    // 🔵 04/11: a ficha saiu de dentro da lista e virou tela, com seta de
    // voltar -- *"não gosto desse modelo de quando clico em 'abrir ficha', ele
    // cria uma pagina em cima da atual. fica feio"*.
    //
    // ⚠️ Vem DEPOIS de `/tecnicos/credenciar` e com o segmento `ficha` no
    // caminho: `/tecnicos/:id` casaria "credenciar" como id.
    //
    // ⚠️ `meta.codigo` é a própria TEC_1.1 -- é a mesma tela, em outro
    // caminho. Quem pode abrir a lista pode abrir a ficha; gravar é que
    // depende da TEC_1.2.
    path: '/tecnicos/ficha/:contatoId',
    name: 'tecnico-ficha',
    component: FichaDoTecnico,
    meta: { codigo: 'TEC_1.1', titulo: 'Ficha do técnico' },
  },
  {
    // Fora de /atendimento: ligação do MicroSIP não é conversa de WhatsApp, e
    // a aba é de auditoria (só do owner), não de atendimento.
    path: '/ligacoes',
    name: 'LIG_1.1',
    component: Ligacoes,
    meta: { codigo: 'LIG_1.1', titulo: 'Ligações' },
  },
  {
    // 🚨 Fora de /atendimento de propósito: é conversa entre ATENDENTES, e
    // nada daqui sai para o cliente. Pendurar em /atendimento faria o log
    // misturar "falou com cliente" e "falou com colega".
    path: '/chat',
    name: 'ATD_6.1',
    component: ChatInterno,
    meta: { codigo: 'ATD_6.1', titulo: 'Chat interno' },
  },
  {
    // Mesmo componente da ATD_1.1: a conversa abre AO LADO da lista, como o
    // 06_Conteudo_das_Telas desenha. Esta rota só entra já com uma escolhida.
    path: '/atendimento/:id',
    name: 'ATD_1.2',
    component: CaixaDeEntrada,
    meta: { codigo: 'ATD_1.2', titulo: 'Conversa', cheio: true },
  },

  {
    // 🚨 Subiu para fase 1 em 07/08. É a única tela que alcança cliente de
    // verdade em lote — o canal é irreversível.
    path: '/informativos',
    name: 'ATD_3.1',
    component: Informativos,
    meta: { codigo: 'ATD_3.1', titulo: 'Informativos' },
  },

  // 🔵 22/10: o PRIMEIRO painel, o da Operacao (opcao A, kanban pelas
  // situacoes da OS). Escolha dele entre tres mockups: "vamos de A somente
  // para a Operacao por enquanto". Os outros tres entram como PNL_1.2/1.3/1.4
  // quando ele decidir -- cada um com tela propria, nao com um filtro aqui.
  {
    path: '/paineis/operacao',
    name: 'PNL_1.1',
    component: PainelOperacao,
    meta: { codigo: 'PNL_1.1', titulo: 'Operação' },
  },

  // 🔵 22/10: a FRM_1.1, o que chega pelos formularios proprios.
  // 🚨 REGISTRO E ROTEADOR ANDAM JUNTOS: tela registrada no backend e ausente
  // daqui aparece no menu e leva para "nao encontrada" -- aconteceu em 06/08
  // com a ATD_5.1, e o `teste_router.py` compara os dois lados desde entao.
  {
    path: '/solicitacoes',
    name: 'FRM_1.1',
    component: Solicitacoes,
    meta: { codigo: 'FRM_1.1', titulo: 'Solicitações' },
  },

  // ---- CAD: cadastro ----
  {
    path: '/cadastro/clientes',
    name: 'CAD_1.1',
    component: Clientes,
    meta: { codigo: 'CAD_1.1', titulo: 'Clientes' },
  },
  {
    // As abas CAD_1.2.1/.2/.3 vivem DENTRO desta tela e não têm rota própria.
    // O código delas existe só como âncora de auditoria — ver o registro.
    path: '/cadastro/contatos',
    name: 'CAD_1.2',
    component: Contatos,
    meta: { codigo: 'CAD_1.2', titulo: 'Contatos' },
  },
  {
    path: '/cadastro/atendentes',
    name: 'CAD_2.1',
    component: Atendentes,
    meta: { codigo: 'CAD_2.1', titulo: 'Atendentes' },
  },
  {
    path: '/cadastro/times',
    name: 'CAD_2.2',
    component: Times,
    meta: { codigo: 'CAD_2.2', titulo: 'Times' },
  },

  // ---- CFG: configuração ----
  //
  // 🚨 AS SEIS VIRARAM ABAS DA CFG_0.1 EM 27/08, e todas as sete rotas montam
  // o MESMO componente: a casca lê `meta.codigo` e abre na aba certa. É isso
  // que faz link antigo, favorito e histórico do navegador continuarem
  // funcionando -- e é isso que mantém `meta.codigo` significando a mesma
  // coisa que sempre significou, para a guarda e para a barra de status.
  //
  // 🚨 ROTA LITERAL ANTES DA COM PARÂMETRO continua valendo, e aqui a ordem
  // também importa por outro motivo: `/config` é prefixo de `/config/canais`.
  // Com `path` exato (sem `:qualquer`), o vue-router não confunde -- mas a
  // ordem escrita é a que se lê, então a genérica fica em cima.
  {
    path: '/config',
    name: 'CFG_0.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_0.1', titulo: 'Configurações' },
  },
  {
    path: '/config/canais',
    name: 'CFG_1.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_1.1', titulo: 'Canais' },
  },
  {
    path: '/config/ia/prompt',
    name: 'CFG_2.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_2.1', titulo: 'IA — prompt' },
  },
  {
    path: '/config/sync',
    name: 'CFG_3.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_3.1', titulo: 'Sincronização' },
  },
  {
    path: '/config/classificacoes',
    name: 'CFG_4.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_4.1', titulo: 'Classificações' },
  },
  {
    path: '/config/automacao',
    name: 'CFG_5.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_5.1', titulo: 'Automação por tipo' },
  },
  {
    // ⚠️ Dentro de Configurações por `aba_de` no registro do backend: a
    // rota existe, e é a casca que a monta como aba.
    //
    // 🚨 ATÉ 25/09 ESTE COMENTÁRIO MENTIA. Nascida em 28/08, esta rota (e as
    // de Atalhos, Eventos, Minha conta, Notificações e Mensagens rápidas, que
    // copiaram o padrão) montava a PÁGINA direto, não a casca: clicar na aba
    // abria a tela sem a barra de abas, e a pessoa ficava presa nela. Achado
    // pelo owner, que não encontrava as Notificações. Agora todas montam a
    // casca -- e `config_rotas.teste.js` reprova se uma voltar a montar direto.
    path: '/config/geral',
    name: 'CFG_7.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_7.1', titulo: 'Geral' },
  },
  {
    path: '/config/atalhos',
    name: 'CFG_6.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_6.1', titulo: 'Atalhos de teclado' },
  },
  {
    path: '/config/eventos',
    name: 'CFG_8.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_8.1', titulo: 'Eventos do WhatsApp' },
  },
  {
    path: '/config/minha-conta',
    name: 'CFG_10.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_10.1', titulo: 'Minha conta' },
  },
  {
    path: '/config/notificacoes',
    name: 'CFG_11.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_11.1', titulo: 'Notificações' },
  },
  {
    // 🟡 Ponta 4 do plano (21/10): a rota de pendências do vínculo tinha
    // dado e não tinha tela. Só owner, como a aba do orçamento.
    path: '/config/vinculo-tecnicos',
    name: 'CFG_14.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_14.1', titulo: 'Vínculo dos técnicos' },
  },
  {
    // 🔵 Decisão 9 dele (19/10): a numeração do orçamento, só do owner.
    path: '/config/orcamento',
    name: 'CFG_13.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_13.1', titulo: 'Orçamento' },
  },
  {
    // 🔵 25/09 (Plano 3): Padrões, Minhas notas e Formulários.
    path: '/config/mensagens-rapidas',
    name: 'CFG_12.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_12.1', titulo: 'Mensagens rápidas' },
  },
  {
    path: '/config/telas',
    name: 'CFG_9.1',
    component: Configuracoes,
    meta: { codigo: 'CFG_9.1', titulo: 'Registro de telas' },
  },

  // ---- auxiliares ----
  {
    path: '/sem-permissao',
    name: 'sem-permissao',
    component: SemPermissao,
    meta: { titulo: 'Sem permissão' },
  },
  {
    path: '/',
    name: 'raiz',
    // sem tela própria: manda para a primeira tela que ESTE usuário enxerga
    redirect: () => ({ path: sessao.telas[0]?.rota || '/atendimento' }),
  },
  {
    path: '/:qualquer(.*)*',
    name: 'nao-encontrada',
    component: NaoEncontrada,
    meta: { titulo: 'Não encontrada' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes: rotas,
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (para) => {
  if (para.meta.publica) {
    // já logado não fica olhando a tela de login
    return autenticado.value ? { path: '/' } : true
  }

  // Recarregou a página com token guardado: recuperar quem é antes de decidir.
  if (!autenticado.value && temToken()) {
    await restaurar()
  }

  if (!autenticado.value) {
    return { name: 'login', query: para.fullPath === '/' ? {} : { destino: para.fullPath } }
  }

  const codigo = para.meta.codigo
  if (codigo && !codigosPermitidos.value.has(codigo)) {
    // O backend não listou esta tela para este usuário. Negar aqui é cortesia:
    // a barreira que vale é a rota do backend, que checa de novo.
    return { name: 'sem-permissao', query: { codigo } }
  }

  return true
})
